import { Button, NavLinkButton, ScrollArea, Separator } from '@ais-ui/ui-core';
import { useState } from 'react';
import { PiCaretLeftThin } from 'react-icons/pi';
import { ROUTES } from '../../routes';
import { SidebarNav } from './SidebarNav';

import { useLocalization } from '@ais-ui/locale-management';
import { SettingsTwoFactorAuthPage } from './Authentication/TwoFactorAuthSettings';
import { SettingsChangePasswordPage } from './Authentication/ChangePasswordSettings';
import { SettingsBookmarksPage } from './Bookmarks/BookmarksSettings';
import { BookmarksAddEdit } from './Bookmarks/BookmarksAddEdit';
import { SettingsEventsPage } from './Events/EventsSettings';
import { GeneralForm } from './General/GeneralForm';
import { AdminForm } from './Admin/AdminForm';
import { NotificationsForm } from './Notifications/NotificationsForm';
import { MapForm } from './Map/MapForm';
import { WMSForm } from './Wms/WMSForm';
import { WMSAddEditService } from './Wms/WMSAddEditService';
import { VectorTileForm } from './VectorTiles/VectorTileForm';
import { VectorTileAddEditService } from './VectorTiles/VectorTileAddEditService';
import { BaseMapForm } from './BaseMap/BaseMapForm';
import { BaseMapAdd } from './BaseMap/BaseMapAdd';
import { SettingsMessagesPage } from './Messages/MessagesSettings';
import { SettingsTargetPage } from './Target/TargetSettings';
import { SettingsReportsPage } from './Reports/ReportsSettings';
import { SettingsPortPage } from './Port/PortSettings';
import { useSettingsPage } from './SettingsPageProvider';
import { useNavigate } from 'react-router';
import { useGeneralForm } from './General/useGeneralForm';
import { useNotificationsForm } from './Notifications/useNotificationsForm';
import { useMapForm } from './Map/useMapForm';
import { useTargetForm } from './Target/useTargetForm';
import { useEventsForm } from './Events/useEventsForm';
import { useMessagesForm } from './Messages/useMessagesForm';
import { useReportsForm } from './Reports/useReportsForm';
import { usePortForm } from './Port/usePortForm';
import { useAdminForm } from './Admin/useAdminForm';
import { usePermissions } from '../Permissions/PermissionsProvider';
import { useWMSSettings } from '../WMSLayers/useWMSSettings';

export enum SettingItem {
  General = 'GENERAL',
  Admin = 'ADMIN',
  WMS = 'WMS',
  VectorTiles = 'VECTOR_TILES',
  Notification = 'NOTIFICATION',
  Map = 'MAP',
  Port = 'PORT',
  BaseMap = 'BASEMAP',
  Bookmarks = 'BOOKMARKS',
  TwoFactorAuth = 'TWO_FACTOR_AUTH',
  ChangePassword = 'CHANGE_PASSWORD',
  Messages = 'MESSAGES',
  Events = 'EVENTS',
  Target = 'TARGET',
  Reports = 'REPORTS',
}

interface SettingsPageProps {
  initialActiveSettingItem?: SettingItem;
}

export function SettingsPage({
  initialActiveSettingItem = SettingItem.General,
}: SettingsPageProps) {
  const { messages } = useLocalization();
  const permissions = usePermissions();
  const [activeSettingItem, setActiveSettingItem] = useState<SettingItem>(
    initialActiveSettingItem
  );

  const wmsSettings = useWMSSettings();

  const navigate = useNavigate();
  const { checkNavigationSafety } = useSettingsPage();

  // Initialize form hooks
  const generalFormHook = useGeneralForm();
  const notificationsFormHook = useNotificationsForm();
  const mapFormHook = useMapForm();
  const targetFormHook = useTargetForm();
  const eventsFormHook = useEventsForm();
  const messagesFormHook = useMessagesForm();
  const reportsFormHook = useReportsForm();
  const portFormHook = usePortForm();
  const adminFormHook = useAdminForm();

  // For WMS, we don't need a form hook since it's action-based, not form-based

  const onSettingItemClick = (settingItem: SettingItem) => {
    const onConfirm = () => {
      setActiveSettingItem(settingItem);
    };

    checkNavigationSafety(onConfirm);
  };

  const sidebarNavItems = [
    {
      id: SettingItem.General,
      title: messages.settingsSidebarGeneralTitle,
      onClick: () => onSettingItemClick(SettingItem.General),
    },
    // Add Admin tab only if user has admin permissions
    ...(permissions.admin
      ? [
          {
            id: SettingItem.Admin,
            title: 'Admin', // Using hardcoded string since this is admin-only
            onClick: () => onSettingItemClick(SettingItem.Admin),
          },
        ]
      : []),
    {
      id: SettingItem.Notification,
      title: messages.settingsSidebarNotificationsTitle,
      onClick: () => onSettingItemClick(SettingItem.Notification),
    },
    {
      id: SettingItem.Map,
      title: messages.settingsSidebarMapTitle,
      onClick: () => onSettingItemClick(SettingItem.Map),
    },
    {
      id: SettingItem.Port,
      title: messages.settingsPortTitle,
      onClick: () => onSettingItemClick(SettingItem.Port),
    },
    {
      id: SettingItem.WMS,
      title: messages.settingsSidebarWmsTitle,
      onClick: () => onSettingItemClick(SettingItem.WMS),
    },
    {
      id: SettingItem.VectorTiles,
      title: messages.settingsSidebarVectorTilesTitle,
      onClick: () => onSettingItemClick(SettingItem.VectorTiles),
    },
    {
      id: SettingItem.BaseMap,
      title: messages.settingsSidebarBaseMapTitle,
      onClick: () => onSettingItemClick(SettingItem.BaseMap),
    },
    {
      id: SettingItem.Target,
      title: messages.settingsTargetTitle,
      onClick: () => onSettingItemClick(SettingItem.Target),
    },
    {
      id: SettingItem.Events,
      title: messages.settingsEventsTitle,
      onClick: () => onSettingItemClick(SettingItem.Events),
    },
    {
      id: SettingItem.Messages,
      title: messages.settingsMessagesTitle,
      onClick: () => onSettingItemClick(SettingItem.Messages),
    },
    {
      id: SettingItem.Reports,
      title: messages.settingsReportsTitle,
      onClick: () => onSettingItemClick(SettingItem.Reports),
    },
    {
      id: SettingItem.TwoFactorAuth,
      title: messages.settingsTwoFactorTitle,
      onClick: () => onSettingItemClick(SettingItem.TwoFactorAuth),
    },
    {
      id: SettingItem.ChangePassword,
      title: messages.settingsChangePasswordTitle,
      onClick: () => onSettingItemClick(SettingItem.ChangePassword),
    },
    {
      id: SettingItem.Bookmarks,
      title: messages.settingsBookmarksTitle,
      onClick: () => onSettingItemClick(SettingItem.Bookmarks),
    },
  ];

  // Get the current page title and subtitle based on active setting
  const getPageInfo = () => {
    switch (activeSettingItem) {
      case SettingItem.General:
        return {
          title: messages.settingsGeneralTitle,
          subtitle: messages.settingsGeneralSubtitle,
        };
      case SettingItem.Admin:
        return {
          title: 'Admin',
          subtitle: 'Configure administrative settings and system preferences',
        };
      case SettingItem.Notification:
        return {
          title: messages.settingsNotificationsTitle,
          subtitle: messages.settingsNotificationsSubtitle,
        };
      case SettingItem.Map:
        return {
          title: messages.settingsMapTitle,
          subtitle: messages.settingsMapSubtitle,
        };
      case SettingItem.Port:
        return {
          title: messages.settingsPortTitle,
          subtitle: messages.settingsPortDescription,
        };
      case SettingItem.WMS:
        return {
          title: messages.settingsWmsTitle,
          subtitle: messages.settingsWmsSubtitle,
        };
      case SettingItem.VectorTiles:
        return {
          title: messages.settingsVectorTilesTitle,
          subtitle: messages.settingsVectorTilesSubtitle,
        };
      case SettingItem.BaseMap:
        return {
          title: messages.settingsBaseMapTitle,
          subtitle: messages.settingsBaseMapSubtitle,
        };
      case SettingItem.Target:
        return {
          title: messages.settingsTargetTitle,
          subtitle: messages.settingsTargetSubtitle,
        };
      case SettingItem.Events:
        return {
          title: messages.settingsEventsTitle,
          subtitle: messages.settingsEventsSubtitle,
        };
      case SettingItem.Messages:
        return {
          title: messages.settingsMessagesTitle,
          subtitle: messages.settingsMessagesSubtitle,
        };
      case SettingItem.Reports:
        return {
          title: messages.settingsReportsTitle,
          subtitle: messages.settingsReportsSubtitle,
        };
      case SettingItem.TwoFactorAuth:
        return {
          title: messages.settingsTwoFactorTitle,
          subtitle: messages.settingsTwoFactorSubtitle,
        };
      case SettingItem.ChangePassword:
        return {
          title: messages.settingsChangePasswordTitle,
          subtitle: messages.settingsChangePasswordSubtitle,
        };
      case SettingItem.Bookmarks:
        return {
          title: messages.settingsBookmarksTitle,
          subtitle: messages.settingsBookmarksSubtitle,
        };
      default:
        return {
          title: '',
          subtitle: '',
        };
    }
  };

  const { title, subtitle } = getPageInfo();

  // Check if current page has unsaved changes and should show save button
  const shouldShowSaveButton =
    activeSettingItem === SettingItem.General ||
    activeSettingItem === SettingItem.Admin ||
    activeSettingItem === SettingItem.Notification ||
    activeSettingItem === SettingItem.Map ||
    activeSettingItem === SettingItem.Port ||
    activeSettingItem === SettingItem.Target ||
    activeSettingItem === SettingItem.Events ||
    activeSettingItem === SettingItem.Messages ||
    activeSettingItem === SettingItem.Reports;

  // Check if current page should show action button (like Add for WMS/VectorTiles/BaseMap)
  const shouldShowActionButton =
    activeSettingItem === SettingItem.WMS ||
    activeSettingItem === SettingItem.VectorTiles ||
    activeSettingItem === SettingItem.BaseMap ||
    activeSettingItem === SettingItem.Bookmarks;

  // Get the appropriate form hook and handlers
  const getCurrentFormHook = () => {
    switch (activeSettingItem) {
      case SettingItem.General:
        return generalFormHook;
      case SettingItem.Notification:
        return notificationsFormHook;
      case SettingItem.Map:
        return mapFormHook;
      case SettingItem.Target:
        return targetFormHook;
      case SettingItem.Events:
        return eventsFormHook;
      case SettingItem.Messages:
        return messagesFormHook;
      case SettingItem.Reports:
        return reportsFormHook;
      case SettingItem.Port:
        return portFormHook;
      case SettingItem.Admin:
        return adminFormHook;
      default:
        return null;
    }
  };

  const handleSaveClick = () => {
    const currentFormHook = getCurrentFormHook();
    if (currentFormHook) {
      currentFormHook.form.handleSubmit(
        currentFormHook.onSubmit as (data: any) => void
      )();
    }
  };

  const currentFormHook = getCurrentFormHook();

  // Helper function to check if any form is currently loading/submitting
  const isFormLoading = (formHook: any) => {
    if (!formHook) return false;
    return formHook.isSubmitting || formHook.isLoading || false;
  };

  return (
    <div className="flex flex-col w-full h-screen bg-gradient-to-b from-primary/5 via-background to-background">
      {/* Fixed Header Section */}
      <div className="flex-shrink-0 w-full flex justify-center">
        <div className="hidden md:block w-[984px]">
          {/* Navigation Back Button */}
          <div className="px-10 pt-8">
            <NavLinkButton
              id="settings-viewMap-button"
              onClick={(e) => {
                e.preventDefault();
                checkNavigationSafety(() => navigate(ROUTES.MAP.$path()));
              }}
              to={ROUTES.MAP.$path()}
              className="p-0 m-0 w-40"
            >
              <PiCaretLeftThin className="p-0 my-2" />
              <div className="pl-2 my-2">{messages.settingsGoBackToMap}</div>
            </NavLinkButton>
          </div>

          <div className="px-10 py-4">
            <div className="flex flex-col space-y-8 lg:flex-row lg:space-x-12 lg:space-y-0 w-full">
              {/* Empty space to align with sidebar */}
              <div className="lg:w-[200px] flex-shrink-0" />
              {/* Header content aligned with main content */}
              <div className="lg:max-w-2xl w-full">
                <div className="flex items-end justify-between w-full">
                  <div className="flex-1">
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-primary/70 uppercase tracking-wide">
                        Settings
                      </div>
                      <h1 className="text-3xl font-bold tracking-tight text-foreground">
                        {title}
                      </h1>
                      <p className="text-base text-muted-foreground leading-relaxed">
                        {subtitle}
                      </p>
                    </div>
                  </div>

                  {shouldShowSaveButton && currentFormHook && (
                    <Button
                      id={`settings-${activeSettingItem.toLowerCase()}-save-button`}
                      onClick={handleSaveClick}
                      disabled={isFormLoading(currentFormHook)}
                      className="ml-4 flex-shrink-0"
                    >
                      {isFormLoading(currentFormHook)
                        ? messages.genericSubmitting
                        : messages.genericSubmit}
                    </Button>
                  )}
                  {shouldShowActionButton &&
                    activeSettingItem === SettingItem.WMS && wmsSettings.wmsInfos.length > 0 && (
                      <WMSAddEditService
                        mode="create"
                        id={'add-service-wms-button'}
                        className="ml-4 flex-shrink-0"
                      />
                    )}
                  {shouldShowActionButton &&
                    activeSettingItem === SettingItem.VectorTiles && (
                      <VectorTileAddEditService
                        mode="create"
                        className="ml-4 flex-shrink-0"
                      />
                    )}
                  {shouldShowActionButton &&
                    activeSettingItem === SettingItem.BaseMap && <BaseMapAdd />}
                  {shouldShowActionButton &&
                    activeSettingItem === SettingItem.Bookmarks && (
                      <BookmarksAddEdit
                        mode="create"
                        className="ml-4 flex-shrink-0"
                      />
                    )}
                </div>
              </div>
            </div>
          </div>
          <div className="px-10 pb-6">
            <Separator></Separator>
          </div>
        </div>
      </div>

      {/* Scrollable Content Section */}
      <ScrollArea className="flex-1 w-full">
        <div className="w-full flex justify-center">
          <div className="hidden md:block w-[984px]">
            <div className="px-10 pb-16">
              <div className="flex flex-col space-y-8 lg:flex-row lg:space-x-12 lg:space-y-0 w-full">
                <aside className="lg:w-[200px] flex-shrink-0">
                  <div className="sticky top-0">
                    <SidebarNav
                      items={sidebarNavItems}
                      activeItem={activeSettingItem}
                    />
                  </div>
                </aside>
                <div className="lg:max-w-2xl w-full">
                {activeSettingItem === SettingItem.General && (
                  <GeneralForm
                    form={generalFormHook.form}
                    getTimeZonesPlusLocalUserTimeZone={
                      generalFormHook.getTimeZonesPlusLocalUserTimeZone
                    }
                  />
                )}
                {activeSettingItem === SettingItem.Notification && (
                  <NotificationsForm form={notificationsFormHook.form} />
                )}
                {activeSettingItem === SettingItem.Map && (
                  <MapForm form={mapFormHook.form} />
                )}
                {activeSettingItem === SettingItem.BaseMap && <BaseMapForm />}
                {activeSettingItem === SettingItem.WMS && <WMSForm />}
                {activeSettingItem === SettingItem.VectorTiles && (
                  <VectorTileForm />
                )}
                {activeSettingItem === SettingItem.Target && (
                  <SettingsTargetPage form={targetFormHook.form} />
                )}
                {activeSettingItem === SettingItem.Events && (
                  <SettingsEventsPage form={eventsFormHook.form} />
                )}
                {activeSettingItem === SettingItem.Messages && (
                  <SettingsMessagesPage form={messagesFormHook.form} />
                )}
                {activeSettingItem === SettingItem.Reports && (
                  <SettingsReportsPage form={reportsFormHook.form} />
                )}
                {activeSettingItem === SettingItem.TwoFactorAuth && (
                  <SettingsTwoFactorAuthPage />
                )}
                {activeSettingItem === SettingItem.ChangePassword && (
                  <SettingsChangePasswordPage />
                )}
                {activeSettingItem === SettingItem.Bookmarks && (
                  <SettingsBookmarksPage />
                )}
                {activeSettingItem === SettingItem.Port && (
                  <SettingsPortPage form={portFormHook.form} />
                )}
                {activeSettingItem === SettingItem.Admin && (
                  <AdminForm form={adminFormHook.form} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>
  </div>
  );
}
